import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { name: "Services", to: "/#services", testid: "nav-services-link" },
  { name: "Work", to: "/work", testid: "nav-work-link" },
  { name: "Pricing", to: "/#pricing", testid: "nav-pricing-link" },
  { name: "About", to: "/#about", testid: "nav-about-link" },
  { name: "Contact", to: "/contact", testid: "nav-contact-link" },
];

export default function NavigationHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "border-b border-navyline/70 bg-ink/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <Link
              key={l.name}
              to={l.to}
              data-testid={l.testid}
              className="text-sm font-medium tracking-wide text-slate-300 transition-colors duration-300 hover:text-gold"
            >
              {l.name}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:block">
          <Link
            to="/start"
            data-testid="nav-book-consultation-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-goldlight"
          >
            Tell Us Your Story
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          className="text-slate-200 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="border-t border-navyline/60 bg-navy/95 px-6 py-6 backdrop-blur-xl lg:hidden">
          <nav className="flex flex-col gap-5" aria-label="Mobile">
            {LINKS.map((l) => (
              <Link
                key={l.name}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                className="font-display text-lg font-semibold text-slate-200 transition-colors duration-300 hover:text-gold"
              >
                {l.name}
              </Link>
            ))}
            <Link
              to="/start"
              data-testid="mobile-book-consultation-cta"
              className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink"
            >
              Tell Us Your Story
              <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
